import type { DisciplineType } from '../services/api/models/DisciplineType';
import type { UserType } from '../services/api/models/UserType';
import { appendUserTypeToDisplayName } from '../constants/userTypes';
import {
  TASK_TYPE_DISCIPLINE_MAP,
  isOrderType,
  type TaskTypeId,
} from '../constants/visitTypes';

export interface ClinicianDisplayNameParts {
  // User fields come from the login profile; clinician fields come from the
  // optional linked provider record used for credentialed display names.
  user_first_name: string;
  user_last_name: string;
  user_email?: string | null;
  user_username?: string | null;
  clinician_first_name?: string | null;
  clinician_last_name?: string | null;
  clinician_credentials?: string | null;
  // `user_types` holds every title, primary first, and is what display reads.
  // `user_type` is the primary alone, kept because sorting still orders on it.
  user_type?: UserType | null;
  user_types?: UserType[] | null;
}

const getFullName = (
  firstName?: string | null,
  lastName?: string | null
): string => [firstName, lastName].filter(s => s?.trim()).join(' ');

/**
 * Canonical display name for a clinician/user across the app. Falls back through
 * linked-clinician name → login name → email → username so an incomplete record
 * never yields a blank string. This is the single source of truth — do not
 * reimplement the `first_name + last_name` concatenation at call sites (it
 * produces blank "  " labels for nameless records, which surface as empty
 * dropdown rows; see ROG-2698).
 */
export const getClinicianDisplayName = (
  clinician: ClinicianDisplayNameParts
): string => {
  const displayName =
    getFullName(
      clinician.clinician_first_name,
      clinician.clinician_last_name
    ) ||
    getFullName(clinician.user_first_name, clinician.user_last_name) ||
    clinician.user_email?.trim() ||
    clinician.user_username?.trim() ||
    '';
  const credentials = clinician.clinician_credentials?.trim();
  // Prefer every title over the primary alone; a record predating the array
  // still has only the singular field to offer.
  const titles = clinician.user_types?.length
    ? clinician.user_types
    : clinician.user_type;

  if (!credentials && !titles) {
    return displayName;
  }

  return appendUserTypeToDisplayName(displayName, titles, credentials);
};

/** Assignable role flags carried by an available-clinician (UserClinic) record. */
export interface ClinicianRoleFlags {
  is_assignable_clinician?: boolean | null;
  is_case_manager?: boolean | null;
  is_clinical_manager?: boolean | null;
  is_approver?: boolean | null;
  disciplines?: DisciplineType[] | null;
}

export type ClinicianRole =
  'clinician' | 'caseManager' | 'clinicalManager' | 'approver';

const rolePredicates: Record<
  ClinicianRole,
  (c: ClinicianRoleFlags) => boolean
> = {
  clinician: c => !!c.is_assignable_clinician,
  caseManager: c => !!c.is_case_manager,
  clinicalManager: c => !!c.is_clinical_manager,
  approver: c => !!c.is_approver,
};

/**
 * A clinician/UserClinic record that can be turned into a select option.
 *
 * `id` is the posting (one row per clinic a person works at); `user_id` is the
 * person. Both travel onto the option because the two are distinct keys.
 */
export type ClinicianSelectInput = ClinicianDisplayNameParts &
  ClinicianRoleFlags & { id: string; user_id?: string | null };

export function clinicianMatchesDiscipline(
  clinician: ClinicianRoleFlags,
  discipline?: DisciplineType
): boolean {
  if (!discipline) return true;
  const disciplines = clinician.disciplines ?? [];
  return disciplines.includes(discipline);
}

/** Discipline required to assign a clinician to this episode-manager task row. */
export function getAssigneeDisciplineForTask(task: {
  task_type: string;
  visit_type?: string | null;
}): DisciplineType | undefined {
  if (
    task.task_type === 'order' ||
    task.task_type === 'patient_communication' ||
    task.task_type === 'misc_task'
  ) {
    return undefined;
  }
  if (
    task.visit_type &&
    task.visit_type in TASK_TYPE_DISCIPLINE_MAP &&
    !isOrderType(task.visit_type as TaskTypeId)
  ) {
    return TASK_TYPE_DISCIPLINE_MAP[task.visit_type as TaskTypeId];
  }
  return undefined;
}

/** Discipline required when creating a task of the given type. */
export function getAssigneeDisciplineForTaskType(
  typeId: string | null | undefined,
  options?: { isOrder?: boolean; isCommunication?: boolean; isMisc?: boolean }
): DisciplineType | undefined {
  if (options?.isOrder || options?.isCommunication || options?.isMisc) {
    return undefined;
  }
  if (!typeId) {
    return undefined;
  }
  if (
    typeId in TASK_TYPE_DISCIPLINE_MAP &&
    !isOrderType(typeId as TaskTypeId)
  ) {
    return TASK_TYPE_DISCIPLINE_MAP[typeId as TaskTypeId];
  }
  return undefined;
}

/** An option for a clinician select, carrying the person behind the posting. */
export interface ClinicianSelectOption {
  value: string;
  label: string;
  /**
   * The person behind this option.
   *
   * `value` is a `UserClinic` id — one row per clinic a person works at — so it
   * cannot identify the human. Anything keyed on the person rather than the
   * posting (workload across all their clinics, for one) needs this.
   */
  user_id: string | null;
}

/**
 * Build options for a clinician/case-manager/approver select from raw
 * available-clinician records. Optionally filters by assignable role, formats
 * labels via {@link getClinicianDisplayName}, and drops any record that has no
 * usable label so a blank row can never reach the dropdown.
 *
 * Works with any select widget that accepts an `options` array (FormSelect,
 * VirtualizedFormSelect, DropdownField). Prefer {@link ClinicianSelect} when you
 * just need a ready-made picker.
 *
 * Every option carries `user_id`. Select widgets read only `value` and `label`,
 * but they hand the option object itself back to a custom renderer, and a
 * renderer that annotates a person — a workload, a caseload — has no other way
 * to reach them.
 */
export const cliniciansToSelectOptions = (
  clinicians: ClinicianSelectInput[],
  role?: ClinicianRole,
  discipline?: DisciplineType
): ClinicianSelectOption[] =>
  clinicians
    .filter(c => (role ? rolePredicates[role](c) : true))
    .filter(c => clinicianMatchesDiscipline(c, discipline))
    .flatMap(c => {
      const label = getClinicianDisplayName(c);
      return label ? [{ value: c.id, label, user_id: c.user_id ?? null }] : [];
    });

// Names that appear more than once (different users, same display name).
// Deduplicate by user_id first to avoid false positives from the same
// clinician appearing in multiple clinics.
export function findDuplicateClinicianNames(
  list: readonly (ClinicianDisplayNameParts & { user_id: string })[]
): Set<string> {
  const seen = new Set<string>();
  const counts = new Map<string, number>();
  for (const c of list) {
    if (seen.has(c.user_id)) continue;
    seen.add(c.user_id);
    const name = getClinicianDisplayName(c);
    counts.set(name, (counts.get(name) || 0) + 1);
  }
  return new Set(
    [...counts].filter(([, count]) => count > 1).map(([name]) => name)
  );
}

export interface ClinicianDropdownOption {
  value: string;
  label: string;
  user_email: string | null | undefined;
  isDup: boolean;
  /**
   * The person behind this option.
   *
   * `value` is a `UserClinic` id — one row per clinic a person works at — so it
   * cannot identify the human. Anything keyed on the person rather than the
   * posting (workload across all their clinics, for one) needs this.
   */
  user_id: string | null;
}

export const cliniciansToDropdownOptions = (
  clinicians: (ClinicianDisplayNameParts & {
    id: string;
    user_id?: string | null;
  })[],
  duplicateNames: Set<string>
): ClinicianDropdownOption[] =>
  clinicians.flatMap(clinician => {
    const name = getClinicianDisplayName(clinician);
    if (!name) return [];
    return [
      {
        value: clinician.id,
        label: name,
        user_email: clinician.user_email ?? null,
        isDup: duplicateNames.has(name),
        user_id: clinician.user_id ?? null,
      },
    ];
  });
